import styled from 'styled-components';
import { WhiteBackgroundRowItem } from './Common.styled';

// Weather Display Container (extends WhiteBackgroundRowItem with weather-specific styling)
export const WeatherDisplayContainer = styled(WhiteBackgroundRowItem)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  padding: 20px;
  width: 100%;
  min-width: 280px;
  max-width: 320px;
  position: relative;
  overflow: hidden;
  height: fit-content;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) => props.theme.colors.glassBackground};
    border-radius: ${(props) => props.theme.effects?.borderRadiusLarge || '20px'};
    opacity: 0.8;
    z-index: -1;
  }

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: ${(props) =>
      props.theme.colors?.glowGradient ||
      'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)'};
    border-radius: 50%;
    opacity: 0;
    transition: ${(props) =>
      props.theme.effects?.transitionMedium ||
      'all 0.25s ease-out'}; /* Simplified for TV performance */
    z-index: -1;
  }

  &:hover::after {
    opacity: 0.2;
    transform: scale(1.05); /* Reduced from 1.2 for TV performance */
  }
`;

// City Name (shared with Time.styled.js - unique text styling for city names)
export const CityName = styled.div`
  font-size: 20px;
  font-weight: 600;
  background: ${(props) =>
    props.theme.colors?.textActive || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  /* text-shadow removed for TV performance */
  text-align: center;
  margin-bottom: 10px;
  transition: ${(props) =>
    props.theme.effects?.transitionMedium ||
    'all 0.25s ease-out'}; /* Simplified for TV performance */

  &:hover {
    background: ${(props) =>
      props.theme.colors?.glowGradient || 'linear-gradient(135deg, #ffffff 0%, #667eea 100%)'};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    transform: scale(1.02); /* Reduced from 1.05 for TV performance */
  }
`;

// Weather Info Container (unique container for weather info)
export const WeatherInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 240px;
`;

// Weather Icon Container - Optimized for TV
export const WeatherIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 10px 0;
  padding: 15px;
  background: ${(props) => props.theme.colors?.glassBackground || 'rgba(255, 255, 255, 0.1)'};
  border-radius: ${(props) => props.theme.effects?.borderRadiusMedium || '12px'};
  backdrop-filter: ${(props) => props.theme.effects?.backdropBlurLight || 'blur(2px)'};
  transition: ${(props) => props.theme.effects?.transitionMedium || 'all 0.25s ease-out'};

  &:hover {
    transform: translateY(-2px); /* Reduced from -5px for better performance */
  }

  img {
    border-radius: ${(props) => props.theme.effects?.borderRadiusMedium || '12px'};
    transition: ${(props) =>
      props.theme.effects?.transitionMedium ||
      'all 0.25s ease-out'}; /* Simplified for TV performance */

    &:hover {
      transform: scale(1.05); /* Reduced from 1.1 for TV performance */
    }
  }
`;

// Temperature Display (unique temperature styling)
export const TemperatureDisplay = styled.div`
  font-size: 28px;
  font-weight: 700;
  background: ${(props) =>
    props.theme.colors?.accentGradient || 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  /* text-shadow removed for TV performance */
  text-align: center;
  margin: 10px 0;
  transition: ${(props) =>
    props.theme.effects?.transitionMedium ||
    'all 0.25s ease-out'}; /* Simplified for TV performance */

  &:hover {
    background: ${(props) =>
      props.theme.colors?.glowGradient || 'linear-gradient(135deg, #ffffff 0%, #ff6b6b 100%)'};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    transform: scale(1.02); /* Reduced from 1.1 for TV performance */
    /* text-shadow removed for TV performance */
  }
`;

// Weather Details Container (unique container for weather details)
export const WeatherDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-top: 10px;
`;

// Weather Detail (unique styling for weather detail items)
export const WeatherDetail = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => props.theme.colors?.textPrimary || '#ffffff'};
  /* text-shadow removed for TV performance */
  text-align: center;
  opacity: 0.9;

  &:hover {
    opacity: 1;
    transform: scale(1.02);
    transition: ${(props) => props.theme.effects?.transitionFast || 'all 0.2s ease'};
  }
`;
